import classNames from "classnames";
import { X } from "lucide-react";

import { FieldTypeIcon } from "../fieldTypeIcons";
import { type EnumOption } from "../FilterDefinition";
import { type Filter } from "../FilterSchema";
import { formatFilterValue } from "../formatFilterValue";
import { comparatorLabel } from "../operators";

import styles from "./FilterChip.module.css";

export interface FilterChipProps {
  /** The filter row this chip represents. */
  filter: Filter;
  /**
   * Human label for `filter.field` (e.g. `"Dataset Id"`). Resolved upstream
   * by `<DataTable.ActiveFilters>` from the `FilterDefinition` list — the
   * chip only consumes a string so its prop surface stays honest.
   */
  fieldName: string;
  /**
   * Pre-resolved enum options for label-vs-value translation when the
   * filter is enum-typed and its definition supplies labeled options
   * (chip text shows ``label`` instead of the opaque wire ``value``).
   * Resolved upstream so this component stays presentational and never
   * touches ``FilterDefinition`` itself.
   */
  enumOptions?: EnumOption[];
  /**
   * Called when the user clicks the `×` segment.
   */
  onRemove: () => void;
  /**
   * Forwarded to `formatFilterValue` to control the multi-value collapse
   * threshold. Defaults to 2 (the helper's default).
   */
  valueLimit?: number;
}

/**
 * One chip in the active filters row. Four segments separated by full-height
 * dividers: `[type-icon Field] | [comparator] | [value] | [×]`. The value
 * segment is omitted entirely for valueless comparators (`EXISTS`,
 * `NOT_EXISTS`, date presets) — the comparator alone carries the semantic.
 *
 * Only the `×` is interactive in v1; the field, comparator, and value
 * segments are non-focusable `<span>`s. Edit-on-click is deferred to a
 * future PR and will swap those spans for `<button>`s when it lands.
 */
export function FilterChip({
  filter,
  fieldName,
  enumOptions,
  onRemove,
  valueLimit,
}: FilterChipProps) {
  const cLabel = comparatorLabel(filter.type, filter.comparator);
  const value = formatFilterValue(filter, { valueLimit, enumOptions });

  return (
    <span
      className={styles.chip}
      role="group"
      aria-label={`${fieldName} ${cLabel}`}
    >
      <span className={classNames(styles.segment, styles.segmentField)}>
        <span className={styles.fieldContent}>
          <FieldTypeIcon type={filter.type} className={styles.icon} />
          <span>{fieldName}</span>
        </span>
      </span>
      <span className={classNames(styles.segment, styles.segmentComparator)}>
        {cLabel}
      </span>
      {value !== null && (
        <span className={classNames(styles.segment, styles.segmentValue)}>
          <span className={styles.valueText}>{value.text}</span>
          {value.more !== undefined && (
            <span className={styles.valueMore}>+{value.more} more</span>
          )}
        </span>
      )}
      <button
        type="button"
        className={classNames(styles.segment, styles.segmentRemove)}
        onClick={onRemove}
        aria-label={`Remove ${fieldName} filter`}
      >
        <X />
      </button>
    </span>
  );
}
